import classNames from 'classnames'
import React, {
  useState,
  useCallback,
  ReactNode,
  isValidElement,
  cloneElement,
  Children,
  FC,
  SVGProps,
  useEffect,
} from 'react'

import Icon from '../icon/Icon'

import styles from './collapse.module.scss'
import ArrowIcon from './icon/arrow.inline.svg'

export interface CollapseItemProps {
  itemKey: string
  label: ReactNode
  children: ReactNode
  className?: string
  headerClassName?: string
  contentClassName?: string
  expandIcon?: FC<SVGProps<SVGElement>>
  expandIconPosition?: 'start' | 'end'
  showArrow?: boolean
  disabled?: boolean
  active?: boolean
  onToggle?: () => void
  expandIconClassName?: string
}

export interface CollapseProps {
  accordion?: boolean
  activeItems?: string[]
  onChange?: (activeKeys: string[]) => void
  defaultActiveKeys?: string | string[] | number | number[]
  children: ReactNode
  className?: string
  resetOnUnmount?: boolean
}

interface CollapseComponent extends FC<CollapseProps> {
  Item: FC<CollapseItemProps>
}

export const CollapseItem: FC<CollapseItemProps> = ({
  itemKey,
  label,
  children,
  className,
  headerClassName,
  contentClassName,
  expandIcon,
  expandIconPosition = 'end',
  showArrow = true,
  disabled = false,
  active = false,
  onToggle,
  expandIconClassName,
}) => {
  const headerId = `collapse-header-${itemKey}`
  const contentId = `collapse-content-${itemKey}`

  const renderIcon = () => (
    <span className={classNames(styles.collapseIcon, active ? styles.rotated : null)}>
      <Icon iconSvg={expandIcon ?? ArrowIcon} className={expandIconClassName} />
    </span>
  )

  return (
    <div className={classNames(className, styles.collapseItem, disabled ? styles.disabled : null)}>
      <div
        className={classNames(styles.collapseHeader, headerClassName, active ? styles.active : null)}
        onClick={onToggle}
        id={headerId}
        aria-controls={contentId}
        aria-expanded={active ? 'true' : 'false'}
        data-testid={headerId}
      >
        {showArrow && expandIconPosition === 'start' && renderIcon()}
        <span className={styles.collapseLabel}>{label}</span>
        {showArrow && expandIconPosition === 'end' && renderIcon()}
      </div>
      <div
        className={classNames(styles.collapseContent, contentClassName, active ? styles.expanded : null)}
        id={contentId}
        aria-labelledby={headerId}
        data-testid={contentId}
        role="region"
      >
        {children}
      </div>
    </div>
  )
}

export const Collapse: CollapseComponent = Object.assign(
  ({
    accordion = false,
    activeItems,
    onChange,
    defaultActiveKeys,
    children,
    className,
    resetOnUnmount = false,
  }: CollapseProps) => {
    // Determine if controlled or uncontrolled
    const isControlled = activeItems !== undefined

    // Initialize state for uncontrolled mode
    const [internalActiveKeys, setInternalActiveKeys] = useState<string[]>(() => {
      if (defaultActiveKeys) {
        if (Array.isArray(defaultActiveKeys)) {
          return defaultActiveKeys.map(String)
        }
        return [String(defaultActiveKeys)]
      }
      return []
    })

    // Reset active keys on unmount if resetOnUnmount is true
    useEffect(() => {
      return () => {
        if (resetOnUnmount && !isControlled) {
          setInternalActiveKeys([])
        }
      }
    }, [resetOnUnmount, isControlled])

    // Use controlled or internal state
    const currentActiveKeys = isControlled ? activeItems : internalActiveKeys

    const handleItemClick = useCallback(
      (itemKey: string) => {
        const isActive = currentActiveKeys.includes(itemKey)
        let newActiveKeys: string[]

        if (accordion) {
          newActiveKeys = isActive ? [] : [itemKey]
        } else {
          newActiveKeys = isActive ? currentActiveKeys.filter(key => key !== itemKey) : [...currentActiveKeys, itemKey]
        }

        if (isControlled) {
          onChange?.(newActiveKeys)
        } else {
          setInternalActiveKeys(newActiveKeys)
        }
      },
      [accordion, currentActiveKeys, isControlled, onChange],
    )

    const renderedChildren = Children.map(children, child => {
      if (isValidElement<CollapseItemProps>(child)) {
        const { itemKey } = child.props
        return cloneElement(child, {
          active: currentActiveKeys.includes(itemKey),
          onToggle: () => {
            handleItemClick(itemKey)
          },
        })
      }
      return child
    })

    return (
      <div
        className={classNames(styles.collapse, className, currentActiveKeys.length === 0 ? styles.allClosed : null)}
        data-testid="collapse-container"
      >
        {renderedChildren}
      </div>
    )
  },
  { Item: CollapseItem },
)

Collapse.Item = CollapseItem
